import {
  ClearLoanDisbursementRequest,
  CreateLoanDisbursementRequest,
  CreateLoanPaymentRequest,
  CreateLoanRequest as GeneratedCreateLoanRequest,
  Loan as GeneratedLoan,
  LendingServiceGetDisbursementsParams,
  LendingServiceGetPaymentsParams,
  LendingServiceGetSalesParams,
  LendingServiceListLoansParams,
  LoanDisbursement,
  LoanPayment,
  LoanSale,
  UpdateLoanDisbursementRequest,
  UpdateLoanRequest,
} from '~/typings/API';
import { convertValues } from '~/util';

import { client } from './client';

interface ListLoansResponse {
  loans: Loan[];
  hasMore: boolean;
}

export type LoanType = 'unsecured' | 'secured';

export type Loan = GeneratedLoan & {
  type?: LoanType;
  collateralBankAccountId?: string;
  collateralLockedAmount?: number;
};

export type CreateLoanRequest = GeneratedCreateLoanRequest & {
  type?: LoanType;
  collateralBankAccountId?: string;
};

export interface ApplyLoanCollateralRequest {
  /** Amount of locked collateral to apply to loan principal, in cents. */
  amount: number;
  /** Currency of the collateral application. Must match the loan currency. */
  currencyCode: string;
  /** Description of the collateral application. Maximum: `127` characters. */
  description?: string;
}

export type ListLoansParams = LendingServiceListLoansParams & {
  query?: string;
  type?: LoanType;
};

interface ListLoansPaymentsResponse {
  payments: LoanPayment[];
  hasMore: boolean;
}

interface ListLoansDisbursementResponse {
  disbursements: LoanDisbursement[];
  hasMore: boolean;
}

export type { LoanPayment, LoanDisbursement, LoanSale };

export class LoanRepository {
  static async create(request: CreateLoanRequest) {
    return client.post<CreateLoanRequest, Loan>('/loans', request).then((response) => convertValues<Loan>(response));
  }

  static async getAll(query?: ListLoansParams) {
    return client.get<ListLoansParams, ListLoansResponse>('/loans', { query }).then((response) => ({
      loans: Array.isArray(response?.loans) ? response.loans.map((e) => convertValues<Loan>(e)) : [],
      hasMore: Boolean(response?.hasMore),
    }));
  }

  static async get(id: string) {
    return client.get<any, Loan>(`/loans/${id}`).then((response) => convertValues<Loan>(response));
  }

  static async update(id: string, request: UpdateLoanRequest) {
    return client
      .patch<UpdateLoanRequest, Loan>(`/loans/${id}`, request)
      .then((response) => convertValues<Loan>(response));
  }

  static async createDisbursement(request: CreateLoanDisbursementRequest) {
    return client
      .post<CreateLoanDisbursementRequest, LoanDisbursement>('/loans/disbursements', request)
      .then((response) => convertValues<LoanDisbursement>(response));
  }

  static async updateDisbursement(id: string, request: UpdateLoanDisbursementRequest) {
    return client
      .patch<UpdateLoanDisbursementRequest, LoanDisbursement>(`/loans/disbursements/${id}`, request)
      .then((response) => convertValues<LoanDisbursement>(response));
  }

  static async clearDisbursement(id: string, request: ClearLoanDisbursementRequest) {
    return client
      .post<ClearLoanDisbursementRequest, LoanDisbursement>(`/loans/disbursements/${id}/clear`, request)
      .then((response) => convertValues<LoanDisbursement>(response));
  }

  static async cancelDisbursement(id: string) {
    return client
      .post<any, LoanDisbursement>(`/loans/disbursements/${id}/cancel`, {})
      .then((response) => convertValues<LoanDisbursement>(response));
  }

  static async getDisbursement(id: string) {
    return client
      .get<any, LoanDisbursement>(`/loans/disbursements/${id}`)
      .then((response) => convertValues<LoanDisbursement>(response));
  }

  static async getAllDisbursements(query?: LendingServiceGetDisbursementsParams) {
    return client
      .get<LendingServiceGetDisbursementsParams, ListLoansDisbursementResponse>('/loans/disbursements', { query })
      .then((response) => ({
        disbursements: Array.isArray(response?.disbursements)
          ? response.disbursements.map((e) => convertValues<LoanDisbursement>(e))
          : [],
        hasMore: Boolean(response?.hasMore),
      }));
  }

  static async createPayment(request: CreateLoanPaymentRequest) {
    return client
      .post<CreateLoanPaymentRequest, LoanPayment>('/loans/payments', request)
      .then((response) => convertValues<LoanPayment>(response));
  }

  static async applyCollateral(id: string, request: ApplyLoanCollateralRequest) {
    return client
      .post<ApplyLoanCollateralRequest, LoanPayment>(`/loans/${id}/apply_collateral`, request, {
        idempotency: crypto.randomUUID(),
      })
      .then((response) => convertValues<LoanPayment>(response));
  }

  static async getPayment(id: string) {
    return client
      .get<unknown, LoanPayment>(`/loans/payments/${id}`)
      .then((response) => convertValues<LoanPayment>(response));
  }

  static async getAllPayments(query?: LendingServiceGetPaymentsParams) {
    return client
      .get<LendingServiceGetPaymentsParams, ListLoansPaymentsResponse>('/loans/payments', { query })
      .then((response) => ({
        payments: Array.isArray(response?.payments) ? response.payments.map((e) => convertValues<LoanPayment>(e)) : [],
        hasMore: Boolean(response?.hasMore),
      }));
  }

  static async getSale(id: string) {
    return client.get<unknown, LoanSale>(`/loans/sales/${id}`).then((response) => convertValues<LoanSale>(response));
  }

  static async getAllSales(query?: LendingServiceGetSalesParams) {
    return client
      .get<LendingServiceGetSalesParams, { loan_sales: LoanSale[]; has_more: boolean }>('/loans/sales', { query })
      .then((response) => ({
        sales: Array.isArray(response?.loan_sales) ? response.loan_sales.map((e) => convertValues<LoanSale>(e)) : [],
        hasMore: Boolean(response?.has_more),
      }));
  }
}
